<template>
  <admin-wrapper
    :straight-title="additionalModuleName"
    :module-enabler="moduleEnabler"
    :inputs="inputs"
    :has-buttons="false"
    :is-form-fetched="isFormFetched"
  >
    <template #[`table.input`]>
      <v-row no-gutters class="mt-8">
        <v-col>
          <v-row no-gutters class="pb-4">
            <v-col cols="9">
              <v-row no-gutters align="baseline">
                <v-col cols="auto" class="pr-4 font-weight-bold">
                  {{ $i18n.t('admin.dataCalculator.configuration') }}
                </v-col>
                <v-col cols="auto">
                  <base-rounded-button
                    icon="mdi-plus"
                    size="x-small"
                    color="success"
                    :tooltip="$i18n.t('admin.dataCalculator.addConfig')"
                    @click="openConfigurationSearchDialog({})"
                  />
                </v-col>
                <v-spacer />
                <data-input v-model="searchValue" v-bind="searchInput" @input="search" />
              </v-row>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col cols="9" style="border: 1px solid rgba(0, 0, 0, 0.12);">
              <v-data-table
                :items="searchedConfigurations"
                :headers="headers"
                :footer-props="{
                  itemsPerPageOptions: [20, 50, 100],
                }"
                :items-per-page="20"
                sort-by="id"
                sort-desc
              >
                <template #[`item.actions`]="{ item }">
                  <dots-menu
                    :items="dotsMenuItems"
                    @edit="openConfigurationSearchDialog(item)"
                    @delete="openDeleteConfigurationDialog(item)"
                  ></dots-menu>
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <main-input-dialog
        v-if="isConfigurationDialogLoaded"
        ref="attribute-fill-input-dialog"
        :is-visible.sync="isConfigurationDialogVisible"
        :title-translation="
          editConfigId ? $i18n.t('admin.dataCalculator.editConfig') : $i18n.t('admin.dataCalculator.addConfig')
        "
        dialog-width="40%"
        :agree-text="editConfigId ? 'dialog.agreeSave' : 'dialog.agreeAdd'"
        :inputs="configurationDialogInputs"
        :loading="isConfigurationDialogLoading"
        @modalClosed="closeAddEditConfig"
        @agree="handleAddEditConfig"
      >
        <template v-for="(input, idx) of configurationDialogInputs" #[`${input.name}.input`]="data">
          <v-col cols="5" :key="idx">
            <data-input
              v-if="data.isDriverSetting"
              v-bind="data"
              @change="onConfigItemChange($event, data.name)"
              v-model="currentConfigItem.driver_settings[data.name]"
            />
            <data-input
              v-else
              v-bind="data"
              @change="onConfigItemChange($event, data.name)"
              v-model="currentConfigItem[data.name]"
            />
          </v-col>
        </template>
      </main-input-dialog>
      <main-dialog
        :is-visible.sync="isDeleteConfigurationDialogVisible"
        :loading="isDeleteConfigurationDialogLoading"
        :title="$i18n.t('admin.dataCalculator.deleteConfigTitle')"
        :body="$i18n.t('admin.dataCalculator.deleteConfigBody')"
        @agree="deleteConfiguration"
        agree-text="dialog.agreeDelete"
      />
    </template>
  </admin-wrapper>
</template>

<script>
import { get, call } from 'vuex-pathify';
import { debounce, get as lodashGet } from 'lodash';

import DotsMenu from '@/components/DotsMenu';
import AdminWrapper from '@/components/AdminWrapper';
import DataInput from '@/components/DataInput';

export default {
  name: 'DataCalculatorSettings',
  components: {
    AdminWrapper,
    DotsMenu,
    MainInputDialog: () => import('@/components/MainInputDialog'),
    MainDialog: () => import('@/components/MainDialog'),
    DataInput,
  },
  data: () => ({
    currentConfigItem: {
      datasource: null,
      attribute: null,
      driver: null,
      driver_settings: {},
    },
    isDeleteConfigurationDialogVisible: false,
    isDeleteConfigurationDialogLoading: false,
    isConfigurationDialogLoaded: false,
    isConfigurationDialogVisible: false,
    isConfigurationDialogLoading: false,
    editConfigId: false,
    isFormFetched: false,
    dotsMenuItems: [
      {
        name: 'edit',
      },
      {
        name: 'delete',
      },
    ],
    searchValue: '',
    searchedConfigurations: [],
  }),
  computed: {
    configurations: get('dataCalculator/configurations'),
    configurationsMapped() {
      return (
        this.configurations?.map(configuration => {
          return {
            ...configuration,
            verboses: {
              datasource: this.getDatasourceVerboseName(configuration.datasource),
              attribute: this.getAttributeName(configuration.datasource, configuration.attribute),
              driver: this.getDriverName(configuration.driver),
            },
          };
        }) || []
      );
    },
    drivers: get('dataCalculator/drivers'),
    dataSources: get('layers/metadata'),
    layers: get('layers/layers'),
    additionalModuleName: get('admin/additionalModules@DATA_CALCULATOR_MODULE.verbose_name'),
    isModuleEnabled: get('admin/settingsValues@data_calculator_module_enabled'),
    moduleEnabler() {
      return {
        name: 'data_calculator_module_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
      };
    },
    layersList() {
      return (
        this.$_objectToArray(this.layers)?.filter(layer => layer.type === 'features_layer' && !!layer.geometry_type) ||
        []
      );
    },
    inputs() {
      return [
        {
          name: 'table',
          hasSuffix: false,
          isCustom: true,
          isNamedSlot: false,
        },
      ];
    },
    driversInputs() {
      const getDefaultProps = type => {
        const notRequiredTypes = ['pattern'];
        return {
          cols: '12',
          ...(notRequiredTypes.includes(type) ? {} : { rules: [v => !!v || v == '0' || 'rules.required'] }),
        };
      };
      return this.drivers.reduce((acc, item) => {
        acc[item.name] = item.settings.map(setting => {
          const type = setting.type === 'layer' ? 'layer' : setting.name;
          return {
            ...getDefaultProps(type),
            name: setting.name,
            isDriverSetting: true,
            translationPath: `admin.dataCalculator.configFields.${type}`,
            subtitle: `admin.dataCalculator.configFields.${type}Subtitle`,
            ...this.getSettingProps(type, setting),
          };
        });
        return acc;
      }, {});
    },
    configurationDialogInputs() {
      return [
        {
          name: 'datasource',
          translationPath: 'admin.dataCalculator.configDatasource',
          cols: '12',
          items:
            Object.values(this.dataSources)?.filter(
              dt => !!dt.geometry_type && ['polygon', 'point', 'linestring'].includes(dt.geometry_type)
            ) || [],
          itemValue: 'name',
          itemText: 'verbose_name',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
        },
        {
          name: 'attribute',
          translationPath: 'admin.dataCalculator.configAttr',
          subtitle: 'admin.dataCalculator.configAttrSubtitle',
          cols: '12',
          itemValue: 'id',
          itemText: 'verbose_name',
          items: this.currentDataSourceAttributes,
          disabled: !this.currentConfigItem['datasource'],
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
        },
        {
          name: 'driver',
          translationPath: 'admin.dataCalculator.configDriver',
          subtitle: 'admin.dataCalculator.configDriverSubtitle',
          disabled: !this.currentConfigItem['datasource'],
          items: this.filteredDrivers,
          itemValue: 'name',
          itemText: 'verbose_name',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
        },
        ...(this.currentConfigItem['driver'] ? this.driversInputs[this.currentConfigItem['driver']] : []),
      ];
    },
    currentDataSourceAttributes() {
      return (
        this.dataSources[this.currentConfigItem['datasource']]?.attributes_schema.attributes?.filter(
          attribute => !attribute.read_only && attribute.data_type?.name !== 'geometric'
        ) || []
      );
    },
    filteredDrivers() {
      return (
        this.drivers.filter(driver =>
          driver.geometry_types
            ? driver.geometry_types?.includes(this.dataSources[this.currentConfigItem['datasource']]?.geometry_type)
            : true
        ) || []
      );
    },
    headers() {
      return [
        {
          text: 'ID',
          value: 'id',
          align: 'center',
        },
        {
          text: this.$i18n.t('admin.dataCalculator.datasource'),
          value: 'verboses.datasource',
          align: 'center',
          searchable: true,
        },
        {
          text: this.$i18n.t('admin.dataCalculator.attribute'),
          value: 'verboses.attribute',
          align: 'center',
          searchable: true,
        },
        {
          text: this.$i18n.t('admin.dataCalculator.driver'),
          value: 'verboses.driver',
          align: 'center',
          searchable: true,
        },
        { text: '', value: 'actions', sortable: false },
      ];
    },
    searchableHeadersValues() {
      return this.headers.filter(header => header.searchable).map(header => header.value);
    },
    searchInput() {
      return {
        dataType: { name: 'text' },
        placeholder: this.$i18n.t('default.find'),
        appendIcon: 'mdi-magnify',
        hideDetails: true,
      };
    },
  },
  watch: {
    isModuleEnabled(nV) {
      if (nV) {
        this.isFormFetched = false;
        this.init();
      }
    },
  },
  methods: {
    getLayers: call('layers/getLayers'),
    getDataSources: call('layers/getLayersMetadata'),
    ...call('dataCalculator/*DataCalculator*'),
    async handleAddEditConfig() {
      this.isConfigurationDialogLoading = true;
      let payload = JSON.parse(JSON.stringify(this.currentConfigItem));
      delete payload.verboses;
      payload.attribute = this.dataSources[payload.datasource]?.attributes_schema.attributes.find(
        attr => attr.id === payload.attribute
      )?.name;
      try {
        if (this.editConfigId) {
          await this.editDataCalculatorConfiguration({
            id: this.editConfigId,
            payload: payload,
          });
        } else {
          await this.addDataCalculatorConfiguration(payload);
        }
        this.search();
        this.isConfigurationDialogVisible = false;
      } finally {
        this.isConfigurationDialogLoading = false;
      }
    },
    closeAddEditConfig() {
      this.currentConfigItem = {
        datasource: null,
        attribute: null,
        driver: null,
        driver_settings: {},
      };
    },
    search(value = this.searchValue) {
      if (!value) this.searchedConfigurations = [...this.configurationsMapped];
      else
        this.searchedConfigurations = [...this.configurationsMapped].filter(dict => {
          return this.searchableHeadersValues.some(header => {
            return lodashGet(dict, header)
              ?.toLocaleLowerCase()
              .includes(value?.toLocaleLowerCase());
          });
        });
    },
    async deleteConfiguration() {
      this.isDeleteConfigurationDialogLoading = true;
      try {
        await this.deleteDataCalculatorConfiguration(this.editConfigId);
        this.search();
      } finally {
        this.isDeleteConfigurationDialogLoading = this.isDeleteConfigurationDialogVisible = false;
      }
    },
    onConfigItemChange(e, name) {
      if (name === 'datasource') {
        this.currentConfigItem.attribute = null;
        if (!this.filteredDrivers.find(driver => driver.name === this.currentConfigItem.driver)) {
          this.currentConfigItem = {
            datasource: e,
            attribute: null,
            driver: null,
            driver_settings: {},
          };
        }
        this.$refs['attribute-fill-input-dialog']?.$refs.form?.resetValidation();
      }
      if (name === 'driver') {
        this.currentConfigItem.driver_settings = {};
      }
    },
    getSettingProps(type, setting) {
      const dict = {
        parameter: {
          items: setting.values ? setting.values : [],
          itemValue: 'name',
          itemText: 'verbose_name',
          dataType: {
            name: 'select',
          },
        },
        layer: {
          items: this.layersList,
          itemValue: 'id',
          itemText: 'name',
          dataType: {
            name: 'select',
          },
        },
        pattern: {
          dataType: {
            name: 'textarea',
          },
        },
      };
      return dict[type];
    },
    openDeleteConfigurationDialog(item) {
      this.editConfigId = item.id;
      this.isDeleteConfigurationDialogVisible = true;
    },
    openConfigurationSearchDialog(data) {
      this.isConfigurationDialogLoaded = true;
      this.isConfigurationDialogVisible = true;
      this.editConfigId = data.id || null;
      const copyData = JSON.parse(JSON.stringify(data));
      delete copyData.id;
      this.currentConfigItem = copyData;
    },
    getAttributeName(datasource, attribute) {
      return this.dataSources[datasource]?.attributes_schema.attributes.find(attr => attr.id === attribute)
        ?.verbose_name;
    },
    getDatasourceVerboseName(item) {
      return this.dataSources[item]?.verbose_name;
    },
    getDriverName(item) {
      return this.drivers.find(driver => driver.name === item)?.verbose_name;
    },
    async init() {
      await Promise.all([
        this.getLayers(),
        this.getDataSources({ with_attributes_schema: true }),
        this.getDataCalculatorConfigurations(),
        this.getDataCalculatorDrivers(),
      ]);
      this.search();
      this.search = debounce(this.search, 500);
      this.isFormFetched = true;
    },
  },
  created() {
    if (this.isModuleEnabled) {
      this.init();
    } else {
      this.isFormFetched = true;
    }
  },
};
</script>
